import React, {useEffect} from 'react';
import wrap from 'wraptile';
import {useDispatch} from 'react-redux';
import {QCSnapshots} from './SnapshotConnected';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
    DeleteSnapshotAction,
    QCSnapshot,
    SnapshotStatus,
    START_FETCH_SNAPSHOTS,
} from './Redux/SnapshotActions';
import CreateArtworkDialogContainer from './CreateArtworkDialogContainer';
import {Video} from '../../QCMedia/Redux/QCMediaActions';
import {frameToSMPTE} from '@accurate-player/accurate-player-core';
import './snapshots.scss';
import './snapshot-icons.scss';
import {DispatchAny} from '@pluto/@types';

export type AspectRatioType = '16:9' | '4:3' | '1:1';

export interface SnapshotContainerProps {
    qcSnapshots: QCSnapshots;
    deleteSnapshot: DeleteSnapshotAction;
    resetSnapshots: () => void;
    video: Video;
    id: number;
}

export const handleMount = wrap(
    (
        dispatch: DispatchAny,
        videoFileId: number,
        onHandleUnmount: () => void
    ) => {
        dispatch({
            type: START_FETCH_SNAPSHOTS,
            videoFileId
        });

        return onHandleUnmount;
    }
);

export const handleUnmount = wrap((resetSnapshots: () => void) => {
    resetSnapshots();
});

export const renderSnapshot = wrap(
    (
        frameRateValue: number,
        dropFrame: boolean,
        videoFileId: number,
        deleteSnapshot: DeleteSnapshotAction,
        id: number,
        item: QCSnapshot
    ) => {
        const {frameNumber} = item;
        const timecode = frameToSMPTE(frameNumber, frameRateValue, dropFrame);

        if (item?.status === SnapshotStatus.FAILED)
            return (
                <div className="snapshot-container__wrapper" key={item.url}>
                    <div className="loading snapshot-wrapper">
                        Snapshot failed to generate
                    </div>
                </div>
            );

        if (item?.status === SnapshotStatus.PENDING)
            return (
                <div className="snapshot-container__wrapper" key={item.url}>
                    <div className="loading snapshot-wrapper">
                        Generating Snapshot
                        <CircularProgress
                            style={{
                                width: '18px',
                                height: '18px',
                            }}
                        />
                    </div>
                </div>
            );

        return (
            <div className="snapshot-container__wrapper" key={item.id}>
                <CreateArtworkDialogContainer
                    url={item.url}
                    deleteSnapshot={deleteSnapshot}
                    videoFileId={videoFileId}
                    id={item.id}
                    qcId={id}
                />
                <div className="snapshot-timecode">{timecode}</div>
            </div>
        );
    }
);

function SnapshotContainer(props: SnapshotContainerProps) {
    const {
        qcSnapshots,
        deleteSnapshot,
        resetSnapshots,
        video,
        id,
    } = props;
    const {dropFrame, frameRate, id: videoId} = video;
    const {denominator, numerator} = frameRate;
    const dispatch = useDispatch();

    const frameRateValue = numerator / denominator;
    const {snapshots} = qcSnapshots;
    const onHandleUnmount = handleUnmount(resetSnapshots);
    const onHandleMount = handleMount(dispatch, videoId, onHandleUnmount);

    useEffect(onHandleMount, []);

    if (!snapshots.length) {
        return (
            <div className="snapshots-container no-snapshots">No snapshots created</div>
        );
    }
    return (
        <div className="snapshots-container">
            {snapshots.map(
                renderSnapshot(
                    frameRateValue,
                    dropFrame,
                    videoId,
                    deleteSnapshot,
                    id
                )
            )}
        </div>
    );
}

export default React.memo(SnapshotContainer);
